import React from 'react';

import AddIcon from '@atlaskit/icon/core/add';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import ProjectIcon from '@atlaskit/icon/core/project';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { useCreateProjectActions } from '@townsquare/create-project/store';
import { archivedTql, followingTql } from '@townsquare/directory-header/utils';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { SideNavProductItem } from '@townsquare/navigation-config/nav4';
import { useProjectCapabilities } from '@townsquare/project-capabilities';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import {
	projectDirectoryArchivedRoute,
	projectDirectoryFollowingRoute,
	projectDirectoryRoute,
} from '@townsquare/routes/project-directory-view';
import {
	projectsAppUpdateViewRoute,
	projectUpdateViewRoute,
} from '@townsquare/routes/project-updates-view';
import { projectRoute } from '@townsquare/routes/project-view';
import { projectsAppSavedViewsUpdatesRoute } from '@townsquare/routes/saved-views-updates-view';
import { fg } from '@townsquare/stat-sig/gate';
import { useCurrentUser } from '@townsquare/user-store';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ExternalSideNavItem } from './ExternalSideNavItem';
import { messages } from './messages';

export const useSideNavItems = (): SideNavItem[] => {
	const intl = useIntl();
	const hasNoWorkspace = useNoWorkspaceExperience();
	const routeDependencies = useRouteDependencies();
	// TODO: This should be moved over to useCreateProjectActions from @townsquare/create-store when this app supports universal create.
	const { openCreateProject } = useCreateProjectActions();

	const currentUser = useCurrentUser().accountId;
	const followingTqlQuery = encodeURI(followingTql(currentUser));
	const isAppUpdateFeedEnabled = fg('atlas_app_update_feeds_enabled');

	const { canCreateProject } = useProjectCapabilities();
	const canCreate = fg('townsquare_m3_group_permissions') ? (canCreateProject ?? false) : true;

	return [
		{
			id: SideNavProductItem.PROJECTS,
			disableCustomisation: true,
			analyticsSubject: AnalyticsSideNavItem.PROJECTS,
			path: generatePath(projectDirectoryRoute, routeDependencies),
			matches: [projectDirectoryRoute, projectRoute],
			name: intl.formatMessage(messages.projectsDirectoryNavLabel),
			testId: 'side-nav-projects',
			Icon: ProjectIcon,
			hoverAction:
				hasNoWorkspace || !canCreate
					? undefined
					: {
							Icon: (iconProps: NewCoreIconProps) => (
								<AddIcon color="currentColor" {...iconProps} size="small" />
							),
							label: intl.formatMessage(messages.createNewProjectButtonLabel),
							onClick: () => {
								openCreateProject({
									redirectOnSave: true,
									skipQueryLoad: true,
								});
							},
						},
		},

		{
			id: SideNavProductItem.PROJECTS_FOLLOWING,
			analyticsSubject: AnalyticsSideNavItem.PROJECTS_FOLLOWING,
			path: hasNoWorkspace
				? generatePath(projectDirectoryRoute, routeDependencies)
				: `${generatePath(
						projectDirectoryFollowingRoute,
						routeDependencies,
					)}?tql=${followingTqlQuery}&viewTab=following`,
			matches: [projectDirectoryFollowingRoute],
			name: intl.formatMessage(messages.followingNavLabel),
			testId: 'side-nav-following',
			Icon: EyeOpenIcon,
		},
		{
			id: SideNavProductItem.UPDATES,
			analyticsSubject: AnalyticsSideNavItem.UPDATES,
			path: generatePath(
				isAppUpdateFeedEnabled ? projectsAppUpdateViewRoute : projectUpdateViewRoute,
				routeDependencies,
			),
			matches: [
				isAppUpdateFeedEnabled ? projectsAppUpdateViewRoute : projectUpdateViewRoute,
				projectsAppSavedViewsUpdatesRoute,
			],
			name: intl.formatMessage(messages.statusUpdatesNavLabel),
			testId: 'side-nav-status-updates',
			Icon: MegaphoneIcon,
			openInNewTab: !isAppUpdateFeedEnabled,
			...(!isAppUpdateFeedEnabled ? { Component: ExternalSideNavItem, isDraggable: true } : {}),
		},
		{
			id: SideNavProductItem.PROJECTS_ARCHIVED,
			analyticsSubject: AnalyticsSideNavItem.PROJECTS_ARCHIVED,
			path: hasNoWorkspace
				? generatePath(projectDirectoryRoute, routeDependencies)
				: `${generatePath(projectDirectoryArchivedRoute, routeDependencies)}?tql=${archivedTql}&viewTab=archived`,
			matches: [projectDirectoryArchivedRoute],
			name: intl.formatMessage(messages.archivedNavLabel),
			testId: 'side-nav-archived',
			Icon: ArchiveBoxIcon,
		},
	].filter(filterUndefined);
};
